// OLMPS v8 — shared "Today" data loader. Used by TodayTable, NowStrip, and
// StepScreen (each mounts its own instance — see docs/OLMPS_V8_SPEC.md "The
// two ORDER tables"). Reads agenda_items (METIS), tasks (KYRS), calendar_events
// (KRNOS), journal_entries (CLIO), user_settings.groups (shelves — same as
// KyrsTool/CompassView), and auto_schedule_log (KYRS auto-scheduler
// verification queue) for "today", plus the FLO snapshot (lib/flo.ts).
// Read-only except for nothing — writes (e.g. marking an agenda item done)
// are done by the callers themselves (StepScreen), not this hook.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '../lib/supabase';
import { getFloSnapshot, FloSnapshot } from '../lib/flo';
import { countPendingAutoScheduleLogs, parseScheduled } from '../lib/autoSchedule';
import { SECTORS, SECTOR_LABELS } from '../domain/olmps';
import { computeLadder, type CompletionEvent, type LadderState } from '../lib/stage-ladder';

// How far back the STAGE ladder engine (src/lib/stage-ladder.ts) reads
// completion events — enough days that the month (goal) and quarter
// (endeavour) rungs have real closed-window history, not just the current
// open window. See docs/OLMPS_V9_SPEC.md §4-6.
const LADDER_WINDOW_DAYS = 120;

// The ladder engine's contract treats ISO timestamps as LOCAL wall-clock
// time (it strips any trailing 'Z' rather than converting timezones — see
// stage-ladder.ts's parseIsoLocal). Supabase timestamps are true UTC
// instants, so they must be reformatted as a local-clock ISO string before
// being handed to computeLadder, or window boundaries (day/week/month/
// quarter) would drift by the device's UTC offset.
function toLocalIso(d: Date): string {
  const p = pad2;
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
}

// ---------- Date/time helpers (small local ports — see CompassView.tsx for
// the same conventions used elsewhere in this shell) -----------------------

export function pad2(n: number): string {
  return n < 10 ? `0${n}` : `${n}`;
}

export function dateKeyOf(d: Date): string {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

// A date-only `scheduled` value (no time component) parses to 00:00 (start
// of day) — this MUST match lib/autoSchedule.ts's canonical `parseScheduled`
// exactly (that function is the single source of truth for `scheduled`
// parsing across the app; a date-only task scheduled today is past-due
// during the day, same as everywhere else).
const parseScheduledDate = parseScheduled;

// Agenda `time` is stored as "H:MM AM/PM" (MetisTool's normalizeTime).
function parseAgendaTimeToday(timeStr: string, base: Date): Date | null {
  const m = /^(\d{1,2}):(\d{2})\s*(AM|PM)$/i.exec(timeStr.trim());
  if (!m) return null;
  let h = Number(m[1]) % 12;
  if (/pm/i.test(m[3])) h += 12;
  const d = new Date(base);
  d.setHours(h, Number(m[2]), 0, 0);
  return d;
}

function parseHHMMToday(hhmm: string, base: Date): Date | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec((hhmm || '').trim());
  if (!m) return null;
  const d = new Date(base);
  d.setHours(Number(m[1]), Number(m[2]), 0, 0);
  return d;
}

export function formatClockTime(d: Date): string {
  let h = d.getHours();
  const m = d.getMinutes();
  const suffix = h >= 12 ? 'PM' : 'AM';
  h = h % 12;
  if (h === 0) h = 12;
  return `${h}:${m < 10 ? `0${m}` : m} ${suffix}`;
}

// Simplified port of KrnosTool's isEventOccurringOnDate — daily/weekly/
// monthly/yearly repeats + exceptions + end_date, "does this occur today".
function eventOccursToday(ev: RawEvent, todayKey: string, today: Date): boolean {
  if (ev.exceptions && ev.exceptions.includes(todayKey)) return false;
  if (ev.end_date && ev.end_date < todayKey) return false;
  if (ev.date > todayKey) return false;
  if (!ev.repeat || ev.repeat === 'none') return ev.date === todayKey;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ev.date);
  if (!m) return false;
  const start = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  if (ev.repeat === 'daily') return true;
  if (ev.repeat === 'weekly') return today.getDay() === start.getDay();
  if (ev.repeat === 'monthly') return today.getDate() === start.getDate();
  if (ev.repeat === 'yearly') return today.getMonth() === start.getMonth() && today.getDate() === start.getDate();
  return false;
}

// ---------- Sector name matching heuristic — same case-insensitive
// "contains" match CompassView.tsx uses to line up task groups / agenda text
// / event titles against SECTOR_LABELS. Kept as a small local port rather
// than importing from CompassView (that file owns the full ring UI, not a
// shared lib) — see docs/OLMPS_V8_SPEC.md "Compass" for the mapping rule. --
function nameMatchesSector(name: string, sectorLabel: string): boolean {
  const a = name.trim().toLowerCase();
  const b = sectorLabel.trim().toLowerCase();
  if (!a || !b) return false;
  return a.includes(b) || b.includes(a);
}

// ---------- Raw row shapes ---------------------------------------------------

interface RawAgenda {
  id: string; // agenda_items primary key — distinct from item_id (MetisTool's
  // stable per-item business key, which can have stale duplicate rows across
  // append-then-dedup writes). Writes must target `id`, not item_id.
  item_id: string;
  text: string | null;
  time: string | null;
  duration: string | null;
  completed: boolean | null;
  sector: string | null;
  task_id: string | null;
  design_id: string | null;
  aim_ref: string | null;
}

interface RawTask {
  id: string;
  title: string | null;
  scheduled: string | null;
  done: boolean | null;
  unlisted: boolean | null;
  group_id: string | null;
  sector: string | null;
  aim_ref: string | null;
  design_id: string | null;
}

interface RawEvent {
  id: string;
  title: string | null;
  date: string;
  start_time: string;
  end_time: string;
  repeat: string | null;
  end_date: string | null;
  exceptions: string[] | null;
}

interface GroupLite {
  id: string;
  title: string;
  isActive: boolean;
  parentId: string | null;
}

// ---------- Public types ------------------------------------------------------

export interface TodayStepItem {
  itemId: string;
  rowId: string;
  text: string;
  time: string; // "H:MM AM/PM" or '' when unset
  durationMinutes: number;
  start: Date | null;
  end: Date | null;
  completed: boolean;
  sector: string | null;
  taskId: string | null;
  designId: string | null;
  aimRef: string | null;
}

/** A single scheduled thing today, normalized across agenda items (METIS),
 *  scheduled tasks (KYRS), and calendar events (KRNOS) for the hour-rail. */
export interface DayEntry {
  id: string;
  title: string;
  start: Date;
  end: Date;
  completed: boolean;
  source: 'agenda' | 'task' | 'event';
  /** Cross-tool link columns (null when unset / events). */
  taskId?: string | null;
  sector?: string | null;
  designId?: string | null;
  aimRef?: string | null;
}

export interface TodayData {
  loading: boolean;
  error: string | null;
  now: Date;
  /** Today's scheduled (KYRS) tasks that are overdue (not done, time already past). */
  overdueCount: number;
  /** Sector labels (SECTOR_LABELS values) currently off trajectory today —
   *  same overdue-ratio thresholds CompassView uses for its Today ring. */
  driftSectors: string[];
  flo: FloSnapshot | null;
  floError: string | null;
  /** Next upcoming scheduled task today, not yet done. */
  nextTask: { title: string; time: Date; shelf: string | null } | null;
  /** The METIS agenda item currently in its time window (not yet completed). */
  currentItem: TodayStepItem | null;
  /** The next upcoming METIS agenda item today (not yet completed). */
  nextItem: TodayStepItem | null;
  /** Count of agenda items scheduled today (regardless of completion). */
  totalItemsToday: number;
  /** Everything scheduled today (agenda + tasks + events) with a resolved
   *  time, sorted chronologically — the hour-rail's source list. */
  dayEntries: DayEntry[];
  /** Raw calendar events for the full calendar (all dates, recurring + exceptions).
   *  Used by week/month views (e.g., KrnosWidget). */
  calendarEvents: RawEvent[];
  /** Count of auto_schedule_log rows still awaiting review (reviewed=false) —
   *  the same itinerary tick/reschedule queue CLIO's verification bell reads
   *  (lib/autoSchedule.ts fetchAutoScheduleLogs). */
  openTonightCount: number;
  journalWrittenToday: boolean;
  /** STAGE ladder engine snapshot (src/lib/stage-ladder.ts) — step/today/
   *  aim/goal/endeavour composition state, built from completed tasks
   *  (KYRS) + completed agenda items (METIS) over the last
   *  LADDER_WINDOW_DAYS as step-rung events; the engine itself performs the
   *  day/week/month/quarter rollup. */
  ladder: LadderState;
}

export interface UseTodayData extends TodayData {
  refresh: () => void;
}

// ---------- Hook ---------------------------------------------------------------

export function useTodayData(userId: string | undefined): UseTodayData {
  const [agenda, setAgenda] = useState<RawAgenda[]>([]);
  const [tasks, setTasks] = useState<RawTask[]>([]);
  const [events, setEvents] = useState<RawEvent[]>([]);
  const [groups, setGroups] = useState<GroupLite[]>([]);
  const [journalWrittenToday, setJournalWrittenToday] = useState(false);
  const [flo, setFlo] = useState<FloSnapshot | null>(null);
  const [floError, setFloError] = useState<string | null>(null);
  const [pendingReviewCount, setPendingReviewCount] = useState(0);
  const [completionEvents, setCompletionEvents] = useState<CompletionEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nowTick, setNowTick] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setNowTick((t) => t + 1), 60000);
    return () => clearInterval(id);
  }, []);

  const load = useCallback(async () => {
    if (!userId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    const todayKey = dateKeyOf(new Date());
    const ladderCutoffIso = new Date(Date.now() - LADDER_WINDOW_DAYS * 24 * 60 * 60 * 1000).toISOString();

    // PROGRESSIVE LOAD: each query commits its slice of state the moment IT
    // resolves — consumers render immediately with whatever has arrived and
    // refine as the rest lands (scores show "–" until their signal exists).
    // The single await at the bottom only drives `loading` (all-settled, for
    // consumers that need the final snapshot, e.g. NowStrip) and error
    // surfacing. Each entry resolves to an error message or null — never
    // rejects — so one failed query can't hide the others' data.
    const parts: PromiseLike<string | null>[] = [
      supabase
        .from('agenda_items')
        .select('id, item_id, text, time, duration, completed, sector, task_id, design_id, aim_ref')
        .eq('user_id', userId)
        .eq('date', todayKey)
        .then((res) => {
          if (res.error) return res.error.message;
          setAgenda((res.data ?? []) as RawAgenda[]);
          return null;
        }),
      supabase
        .from('tasks')
        .select('id, title, scheduled, done, unlisted, group_id, sector, aim_ref, design_id')
        .eq('user_id', userId)
        .not('scheduled', 'is', null)
        .then((res) => {
          if (res.error) return res.error.message;
          setTasks((res.data ?? []) as RawTask[]);
          return null;
        }),
      supabase
        .from('calendar_events')
        .select('id, title, date, start_time, end_time, repeat, end_date, exceptions')
        .eq('user_id', userId)
        .then((res) => {
          if (res.error) return res.error.message;
          setEvents((res.data ?? []) as RawEvent[]);
          return null;
        }),
      supabase
        .from('journal_entries')
        .select('id')
        .eq('user_id', userId)
        .eq('date', todayKey)
        .limit(1)
        .then((res) => {
          if (res.error) return res.error.message;
          setJournalWrittenToday(((res.data ?? []) as unknown[]).length > 0);
          return null;
        }),
      supabase
        .from('user_settings')
        .select('groups')
        .eq('user_id', userId)
        .maybeSingle()
        .then((res) => {
          if (res.error) return res.error.message;
          const rawGroups = Array.isArray(res.data?.groups) ? (res.data!.groups as any[]) : [];
          setGroups(
            rawGroups.map((g) => ({
              id: g.id,
              title: g.title ?? '',
              isActive: g.isActive !== false,
              parentId: g.parentId ?? null,
            }))
          );
          return null;
        }),
      // FLO snapshot (the slowest part — its own 6-query fan-out). Its error
      // is reported via floError, not the page-level error, same as before.
      getFloSnapshot(supabase, userId)
        .then((snap) => {
          setFlo(snap);
          setFloError(null);
          return null as string | null;
        })
        .catch((e) => {
          setFlo(null);
          setFloError(e instanceof Error ? e.message : 'Failed to load FLO');
          return null;
        }),
      countPendingAutoScheduleLogs(userId).then((n) => {
        setPendingReviewCount(n);
        return null as string | null;
      }),
      // STAGE ladder engine input — completed tasks (KYRS) + completed METIS
      // agenda items, last LADDER_WINDOW_DAYS. `updated_at` is the
      // completion-time proxy (tasks has no completed_at column — same
      // convention as lib/flo.ts's Flow calc and spectrum-data.ts's KYRS rep
      // deriver). The two arrive together since they feed one state slice.
      Promise.all([
        supabase
          .from('tasks')
          .select('id, updated_at, unlisted')
          .eq('user_id', userId)
          .eq('done', true)
          .gte('updated_at', ladderCutoffIso),
        supabase
          .from('agenda_items')
          .select('id, updated_at')
          .eq('user_id', userId)
          .eq('completed', true)
          .gte('updated_at', ladderCutoffIso),
      ]).then(([taskCompletionsRes, agendaCompletionsRes]) => {
        if (taskCompletionsRes.error) return taskCompletionsRes.error.message;
        if (agendaCompletionsRes.error) return agendaCompletionsRes.error.message;
        const taskEvents: CompletionEvent[] = (
          (taskCompletionsRes.data ?? []) as Array<{ updated_at: string | null; unlisted: boolean | null }>
        )
          .filter((t) => !t.unlisted && !!t.updated_at)
          .map((t) => ({ rung: 'step', timestamp: toLocalIso(new Date(t.updated_at!)), qualified: true }));
        const agendaCompletionEvents: CompletionEvent[] = (
          (agendaCompletionsRes.data ?? []) as Array<{ updated_at: string | null }>
        )
          .filter((a) => !!a.updated_at)
          .map((a) => ({ rung: 'step', timestamp: toLocalIso(new Date(a.updated_at!)), qualified: true }));
        setCompletionEvents([...taskEvents, ...agendaCompletionEvents]);
        return null;
      }),
    ];

    try {
      const errs = await Promise.all(parts);
      setError(errs.find((e) => e !== null) ?? null);
    } catch (e) {
      // A rejected query or unexpected throw must never leave the home stuck
      // on a spinner — surface it and let the finally clear loading.
      console.error('[useTodayData] load failed:', e);
      setError(e instanceof Error ? e.message : 'Failed to load today data');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    void load();
  }, [load]);

  const computed = useMemo(() => {
    const now = new Date();
    const todayKey = dateKeyOf(now);

    let overdueCount = 0;
    let nextTask: { title: string; time: Date; shelf: string | null } | null = null;
    let bestNext: Date | null = null;
    for (const t of tasks) {
      if (t.done || t.unlisted) continue;
      const sd = parseScheduledDate(t.scheduled);
      if (!sd || dateKeyOf(sd) !== todayKey) continue;
      if (sd < now) {
        overdueCount++;
      } else if (!bestNext || sd < bestNext) {
        bestNext = sd;
        const shelf = t.group_id ? groups.find((g) => g.id === t.group_id)?.title ?? null : null;
        nextTask = { title: t.title || 'Untitled task', time: sd, shelf: shelf || null };
      }
    }

    const items: TodayStepItem[] = agenda
      .filter((a) => !!a.text)
      .map((a) => {
        const start = a.time ? parseAgendaTimeToday(a.time, now) : null;
        const durationMinutes = Number.parseInt(a.duration ?? '', 10);
        const dur = Number.isFinite(durationMinutes) && durationMinutes > 0 ? durationMinutes : 30;
        const end = start ? new Date(start.getTime() + dur * 60000) : null;
        return {
          itemId: a.item_id,
          rowId: a.id,
          text: a.text ?? '',
          time: a.time ?? '',
          durationMinutes: dur,
          start,
          end,
          completed: !!a.completed,
          sector: a.sector ?? null,
          taskId: a.task_id ?? null,
          designId: a.design_id ?? null,
          aimRef: a.aim_ref ?? null,
        };
      })
      .sort((a, b) => (a.start?.getTime() ?? Infinity) - (b.start?.getTime() ?? Infinity));

    const currentItem =
      items.find((i) => i.start && i.end && i.start <= now && now < i.end && !i.completed) ?? null;
    const nextItem = items.find((i) => i.start && i.start > now && !i.completed) ?? null;

    // Orient row: per-sector "off trajectory" status for TODAY-level data —
    // a minimal, Today-ring-only port of CompassView's buildNodeStates /
    // computeTodayBucket / ringColorFromBucket (see that file for the full
    // five-ring version). A sector is off trajectory when its Today bucket
    // has at least one overdue item, same threshold CompassView uses for
    // amber/red at the Today ring.
    const driftSectors: string[] = [];
    for (const sector of SECTORS) {
      const label = SECTOR_LABELS[sector];
      const group = groups.find((g) => g.isActive && !g.parentId && nameMatchesSector(g.title, label)) ?? null;
      let total = 0;
      let overdue = 0;
      if (group) {
        for (const t of tasks) {
          if (t.unlisted || t.group_id !== group.id) continue;
          const sd = parseScheduledDate(t.scheduled);
          if (!sd || dateKeyOf(sd) !== todayKey) continue;
          total++;
          if (!t.done && sd < now) overdue++;
        }
      }
      for (const a of agenda) {
        if (!a.text || !nameMatchesSector(a.text, label)) continue;
        total++;
        if (a.completed) continue;
        const t = a.time ? parseAgendaTimeToday(a.time, now) : null;
        if (t && t < now) overdue++;
      }
      for (const e of events) {
        if (!e.title || !nameMatchesSector(e.title, label)) continue;
        if (!eventOccursToday(e, todayKey, now)) continue;
        total++;
      }
      if (total > 0 && overdue > 0) driftSectors.push(label);
    }

    const dayEntries: DayEntry[] = [];
    for (const i of items) {
      if (!i.start || !i.end) continue;
      dayEntries.push({ id: `agenda-${i.rowId}`, title: i.text, start: i.start, end: i.end, completed: i.completed, source: 'agenda', taskId: i.taskId, sector: i.sector, designId: i.designId, aimRef: i.aimRef });
    }
    for (const t of tasks) {
      if (t.unlisted) continue;
      const sd = parseScheduledDate(t.scheduled);
      if (!sd || dateKeyOf(sd) !== todayKey) continue;
      dayEntries.push({
        id: `task-${t.id}`,
        title: t.title || 'Untitled task',
        start: sd,
        end: new Date(sd.getTime() + 30 * 60000),
        completed: !!t.done,
        source: 'task',
        taskId: t.id,
        sector: t.sector ?? null,
        designId: t.design_id ?? null,
        aimRef: t.aim_ref ?? null,
      });
    }
    for (const e of events) {
      if (!e.title || !eventOccursToday(e, todayKey, now)) continue;
      const start = parseHHMMToday(e.start_time, now);
      const end = e.end_time ? parseHHMMToday(e.end_time, now) : null;
      if (!start) continue;
      dayEntries.push({
        id: `event-${e.id}`,
        title: e.title,
        start,
        end: end && end > start ? end : new Date(start.getTime() + 30 * 60000),
        completed: false,
        source: 'event',
      });
    }
    dayEntries.sort((a, b) => a.start.getTime() - b.start.getTime());

    return {
      now,
      overdueCount,
      driftSectors,
      nextTask,
      currentItem,
      nextItem,
      totalItemsToday: items.length,
      dayEntries,
    };
    // nowTick forces a periodic recompute of "current/overdue" state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tasks, agenda, events, groups, nowTick]);

  // STAGE ladder — pure engine call (src/lib/stage-ladder.ts), recomputed
  // whenever completion events reload or the clock ticks (so the live pace
  // projection for the current open window stays fresh).
  const ladder = useMemo(
    () => computeLadder(completionEvents, {}, toLocalIso(new Date())),
    // nowTick forces the current-window projection to refresh periodically.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [completionEvents, nowTick]
  );

  return {
    loading,
    error,
    now: computed.now,
    overdueCount: computed.overdueCount,
    driftSectors: computed.driftSectors,
    flo,
    floError,
    nextTask: computed.nextTask,
    currentItem: computed.currentItem,
    nextItem: computed.nextItem,
    totalItemsToday: computed.totalItemsToday,
    dayEntries: computed.dayEntries,
    calendarEvents: events,
    openTonightCount: pendingReviewCount,
    journalWrittenToday,
    ladder,
    refresh: () => void load(),
  };
}

// ---------- Shared row-summary formatters (used by both TodayTable and
// StepScreen's collapsed lines — see docs/OLMPS_V8_SPEC.md "Step screen"). ---

export function formatOrientSummary(data: TodayData): string {
  const n = data.driftSectors.length;
  if (n === 0) return 'On trajectory · ASTRA →';
  if (n === 1) return `${data.driftSectors[0]} off trajectory · ASTRA →`;
  return `${n} sectors off trajectory · ASTRA →`;
}

export function formatRegulateSummary(data: TodayData): string {
  if (!data.flo) return data.floError ? 'FLO unavailable' : 'Loading FLO…';
  return `Charge ${data.flo.charge.toFixed(1)} · Load ${Math.round(data.flo.load)} · Flow ${Math.round(data.flo.flow.score)}`;
}

export function formatDirectSummary(data: TodayData): string {
  if (!data.nextTask) return 'Nothing scheduled next';
  const parts = [data.nextTask.title];
  if (data.nextTask.shelf) parts.push(data.nextTask.shelf);
  parts.push(formatClockTime(data.nextTask.time));
  return parts.join(' · ');
}

export function formatExecuteSummary(data: TodayData): string {
  const item = data.currentItem ?? data.nextItem;
  return item ? item.text : 'Nothing scheduled today';
}

export function formatReviseSummary(data: TodayData): string {
  return `${data.openTonightCount} item(s) to tick/reschedule · journal ${data.journalWrittenToday ? 'written' : 'not written'}`;
}

/** Compact "state · detail" for a single STAGE ladder rung snapshot — e.g.
 *  "forming · 2/4" (still filling the window) or "maintaining · on track" /
 *  "maintaining · behind pace" (already acquired, tracking live pace). */
export function formatLadderRungState(rung: LadderState['rungs'][keyof LadderState['rungs']]): string {
  const cw = rung.currentWindow;
  if (rung.state === 'unformed' || rung.state === 'forming') {
    return `${rung.state} · ${cw.count}/${cw.needed}`;
  }
  return `${rung.state} · ${cw.onTrack ? 'on track' : 'behind pace'}`;
}

/** The Today row's ladder line — "Today: forming · 2/4" — from
 *  ladder.rungs.today. Appended to the Execute row (the day's step-level
 *  work) in TodayTable, since completing today's steps is what rolls up
 *  into the today rung. */
export function formatTodayRungSummary(data: TodayData): string {
  return `Today: ${formatLadderRungState(data.ladder.rungs.today)}`;
}
